import { Card, CardContent, Grid, Typography, IconButton, Box } from '@mui/material';
import React from 'react';
import { useSnackbar } from 'notistack';
import { apiConstants, postGeneric } from '../../common/constants';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// Shared by the Carrier Logs tool page and the shipment details page.

// Carrier logs are stored against the order's reference number only.
export async function fetchCarrierLogs(reference) {
    const response = await postGeneric(`${apiConstants.REPORT_GENERIC_SEARCH}`, {
        "collection": "clickpost_request_logs",
        "andfilter": [
            { field: "referenceNumber", operator: "eq", value: reference }
        ]
    });
    return response?.data?.count > 0 ? response?.data?.data : [];
}

const SECTIONS = [
    { title: 'User Request', key: 'user_request' },
    { title: 'Carrier Response', key: 'clickpost_response' },
    { title: 'Courier Partner Request', key: 'courier_partner_request' },
    { title: 'Courier Partner Response', key: 'courier_partner_response' },
];

// Function to render JSON with bold keys
const renderJsonWithBoldKeys = (jsonData, sectionKey) => {
    try {
        let parsedData;
        if (typeof jsonData === 'string') {
            // Handle courier partner data formatting
            if (sectionKey === 'courier_partner_request' || sectionKey === 'courier_partner_response') {
                let formattedData = jsonData
                    .replace(/'/g, '"')
                    .replace(/None/g, 'null')
                    .replace(/True/g, 'true')
                    .replace(/False/g, 'false');
                parsedData = JSON.parse(formattedData);
            } else {
                parsedData = JSON.parse(jsonData);
            }
        } else {
            parsedData = jsonData;
        }

        // Convert to formatted JSON string and then split by lines
        const jsonString = JSON.stringify(parsedData, null, 2);
        const lines = jsonString.split('\n');

        return lines.map((line, index) => {
            // Check if line contains a key (has quotes followed by colon)
            const keyMatch = line.match(/^(\s*)"([^"]+)"(\s*:\s*)/);
            if (keyMatch) {
                const [, leadingSpaces, key, colonAndSpaces] = keyMatch;
                const restOfLine = line.substring(keyMatch[0].length);

                // Check if the value part is a number
                const numericMatch = restOfLine.match(/^(\s*)(-?\d+\.?\d*)(.*)/);
                if (numericMatch) {
                    const [, valueSpaces, numericValue, afterValue] = numericMatch;
                    return (
                        <span key={index}>
                            {leadingSpaces}
                            <span style={{ fontWeight: 'bold' }}>"{key}"</span>
                            {colonAndSpaces}
                            {valueSpaces}
                            <span style={{ color: 'blue' }}>{numericValue}</span>
                            {afterValue}
                            {'\n'}
                        </span>
                    );
                } else {
                    return (
                        <span key={index}>
                            {leadingSpaces}
                            <span style={{ fontWeight: 'bold' }}>"{key}"</span>
                            {colonAndSpaces}
                            {restOfLine}
                            {'\n'}
                        </span>
                    );
                }
            }
            return <span key={index}>{line}{'\n'}</span>;
        });
    } catch (error) {
        // If parsing fails, return original data as string
        return typeof jsonData === 'string' ? jsonData : JSON.stringify(jsonData, null, 2);
    }
};

export default function CarrierLogPanels({ logs }) {
    const { enqueueSnackbar } = useSnackbar();

    const handleCopy = async (data, sectionName) => {
        try {
            await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
            enqueueSnackbar(`${sectionName} data copied to clipboard!`, { variant: 'success' });
        } catch (error) {
            enqueueSnackbar('Failed to copy data', { variant: 'error' });
        }
    };

    const renderLogSection = (title, data, sectionKey) => {
        if (!data) return null;

        return (
            <Grid item xs={12} md={6} sx={{ marginBottom: 2 }} key={sectionKey}>
                <Card sx={{ height: '100%' }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'textPrimary' }}>
                                {title}
                            </Typography>
                            <IconButton
                                onClick={() => handleCopy(data, title)}
                                sx={{
                                    backgroundColor: 'primary.main',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: 'primary.dark',
                                    }
                                }}
                                size="small"
                            >
                                <ContentCopyIcon fontSize="small" />
                            </IconButton>
                        </Box>
                        <Box sx={{
                            backgroundColor: '#f5f5f5',
                            padding: 2,
                            borderRadius: 1,
                            maxHeight: '400px',
                            overflow: 'auto'
                        }}>
                            <Typography
                                component="pre"
                                sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '12px',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                }}
                            >
                                {renderJsonWithBoldKeys(data, sectionKey)}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
        );
    };

    if (!logs || logs.length === 0) return null;

    return (
        <Grid container spacing={2}>
            {logs.map((logEntry, index) => (
                <React.Fragment key={index}>
                    {SECTIONS.map((s) => renderLogSection(s.title, logEntry.logs?.[s.key], s.key))}
                </React.Fragment>
            ))}
        </Grid>
    );
}
